import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAgents } from "@/lib/useAgents";
import type { BuyerTermsForm } from "@/lib/buyerTerms";

const selectClass =
  "h-10 sm:h-11 w-full rounded-lg border border-border bg-background px-3 text-sm sm:text-[15px] text-foreground focus:outline-hidden focus:ring-2 focus:ring-ring";

interface Props {
  value: BuyerTermsForm;
  onChange: (next: BuyerTermsForm) => void;
  /** Id prefix so the same fields can sit on different pages without clashing */
  idPrefix?: string;
}

/** City, credit terms, transporter and agent of a buyer */
export function BuyerTermsFields({ value, onChange, idPrefix = "bt" }: Props) {
  const agents = useAgents();
  const set = (patch: Partial<BuyerTermsForm>) => onChange({ ...value, ...patch });
  // An agent who was deactivated after being linked still has to show up in the list
  const linkedMissing = value.agent_id && !agents.some((a) => a.id === value.agent_id);

  return (
    <div className="space-y-4 rounded-xl border border-border p-4 bg-muted/20">
      <p className="text-sm font-semibold text-foreground">Trade &amp; credit terms</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
        <div className="space-y-2">
          <Label htmlFor={`${idPrefix}_city`}>Market / City</Label>
          <Input id={`${idPrefix}_city`} placeholder="e.g. Surat, Tirupur" value={value.city} onChange={(e) => set({ city: e.target.value })} />
        </div>
        <div className="space-y-2">
          <Label htmlFor={`${idPrefix}_agent`}>Agent / Broker</Label>
          <select id={`${idPrefix}_agent`} className={selectClass} value={value.agent_id} onChange={(e) => set({ agent_id: e.target.value })}>
            <option value="">-- Direct (no agent) --</option>
            {linkedMissing && <option value={value.agent_id}>(inactive agent)</option>}
            {agents.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
                {a.city ? ` · ${a.city}` : ""} ({Number(a.commission_pct)}%)
              </option>
            ))}
          </select>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
        <div className="space-y-2">
          <Label htmlFor={`${idPrefix}_limit`}>Credit limit (₹)</Label>
          <Input id={`${idPrefix}_limit`} type="number" min={0} step="1" value={value.credit_limit} onChange={(e) => set({ credit_limit: e.target.value })} />
        </div>
        <div className="space-y-2">
          <Label htmlFor={`${idPrefix}_days`}>Credit days</Label>
          <Input id={`${idPrefix}_days`} type="number" min={0} max={365} step="1" value={value.credit_days} onChange={(e) => set({ credit_days: e.target.value })} />
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
        <div className="space-y-2">
          <Label htmlFor={`${idPrefix}_terms`}>Payment terms</Label>
          <Input id={`${idPrefix}_terms`} placeholder="e.g. 30% advance, balance on delivery" value={value.payment_terms} onChange={(e) => set({ payment_terms: e.target.value })} />
        </div>
        <div className="space-y-2">
          <Label htmlFor={`${idPrefix}_transport`}>Preferred transporter</Label>
          <Input id={`${idPrefix}_transport`} value={value.transporter} onChange={(e) => set({ transporter: e.target.value })} />
        </div>
      </div>
    </div>
  );
}
